import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import ChevronIcon from "@static/core/icons/icon-chevron.svg?react";
import ViewIcon from "@static/core/icons/icon-view.svg?react";
import { TransactionDetail } from "@/type/core/transaction/transaction.types";
import DetailItem from "@components/core/items/DetailItem";
import BasicButton from "@components/core/buttons/BasicButton";
import PreviewMailModal from "@components/core/modals/PreviewMailModal";
import { requestGetMailPreview } from "@state/util/UtilEffects";
import { toastError } from "@utils/helpers/toast-helper";
import {
  MailConfiguration,
  Sender,
} from "@type/core/dashboard/dashboard.types";

interface TransactionMailsSectionProps {
  transaction: TransactionDetail;
  className?: string;
}

const TransactionMailsSection: FunctionComponent<
  TransactionMailsSectionProps
> = ({ transaction, className = "" }) => {
  const { t } = useTranslation();
  const [
    isPreviewInvitationMailModalOpen,
    setIsPreviewInvitationMailModalOpen,
  ] = useState<boolean>(false);
  const [
    isPreviewEndOfTransactionMailModalOpen,
    setIsPreviewEndOfTransactionMailModalOpen,
  ] = useState<boolean>(false);
  const [previewMail, setPreviewMail] = useState<string>("");
  const hasExtraRecipients =
    transaction.extraRecipients && transaction.extraRecipients.length > 0;

  const getMailPreview = (
    mailConfiguration: MailConfiguration,
    type: string,
  ): void => {
    void requestGetMailPreview({
      upperEntityId: type,
      dto: {
        message: mailConfiguration.message,
        signature: mailConfiguration.signature,
      },
    }).then((result) => {
      if (result.ok && result.data) {
        void result.data.text().then((text: string) => {
          setPreviewMail(text);
        });
      } else {
        toastError(t("Erreur lors de la previsualisation"));
      }
    });
  };

  const buildSender = (sender?: Sender): string => {
    if (!sender) {
      return "";
    }
    let buildedSender = "";
    switch (sender.type) {
      case "alias":
        buildedSender = transaction.officeName ?? "";
        break;
      case "jesignexpert":
        buildedSender = "Jesignexpert";
        break;
      case "custom":
        buildedSender = sender.value;
        break;
    }

    return buildedSender?.length > 0
      ? buildedSender + " <noreply@jesignexpert.com>"
      : "<noreply@jesignexpert.com>";
  };

  return (
    <div className={`app-section-transaction-signatories ${className}`}>
      <div className="p-24">
        <h2 className="d-flex align-items-center text-black mr-8 mb-0">
          <ChevronIcon className="rotate-270 text-primary icon-30" />
          {t("transaction.detail.mail")}
        </h2>
      </div>
      <div className="px-24 pb-24">
        {transaction && transaction.type === "signature" && (
          <DetailItem
            noTooltip
            description={
              <BasicButton
                variant="transparent"
                icon={<ViewIcon />}
                text={t("offices.mailModel.form.invitation.preview.label")}
                onClick={() => {
                  if (transaction.invitationMailConfiguration) {
                    setIsPreviewInvitationMailModalOpen(true);
                    getMailPreview(
                      transaction.invitationMailConfiguration,
                      "invitationMailPreview",
                    );
                  }
                }}
              />
            }
            className={hasExtraRecipients ? "" : "p-0 mh-auto no-border"}
          />
        )}
        <DetailItem
          noTooltip
          description={
            <BasicButton
              variant="transparent"
              icon={<ViewIcon />}
              text={t("offices.mailModel.form.endOfTransaction.preview.label")}
              onClick={() => {
                if (transaction.endOfTransactionMailConfiguration) {
                  setIsPreviewEndOfTransactionMailModalOpen(true);
                  getMailPreview(
                    transaction.endOfTransactionMailConfiguration,
                    "endOfTransactionMailPreview",
                  );
                }
              }}
            />
          }
          className={hasExtraRecipients ? "" : "p-0 mh-auto no-border"}
        />
        {transaction.extraRecipients &&
          transaction.extraRecipients.length > 0 && (
            <>
              <DetailItem
                noTooltip
                title={t("transaction.detail.recipient")}
                description={transaction.extraRecipients
                  .map((recipient) =>
                    recipient.firstname
                      ? `${recipient.firstname} (${recipient.email})`
                      : recipient.email,
                  )
                  .join(", ")}
                descriptionGrey
                descriptionBold
                className="no-border"
                dataTest="recipients"
              />
            </>
          )}
      </div>
      {transaction && transaction.type === "signature" && (
        <PreviewMailModal
          visible={isPreviewInvitationMailModalOpen}
          onCancel={() => setIsPreviewInvitationMailModalOpen(false)}
          sender={buildSender(transaction.invitationMailConfiguration?.sender)}
          subject={
            transaction.invitationMailConfiguration &&
            transaction.invitationMailConfiguration.subject !== ""
              ? transaction.invitationMailConfiguration.subject
              : t(
                  "signature.accordion.parametersForm.preview.modal.subject.value",
                )
          }
          previewMail={previewMail}
        />
      )}
      <PreviewMailModal
        visible={isPreviewEndOfTransactionMailModalOpen}
        onCancel={() => setIsPreviewEndOfTransactionMailModalOpen(false)}
        sender={buildSender(
          transaction.endOfTransactionMailConfiguration?.sender,
        )}
        subject={
          transaction.endOfTransactionMailConfiguration &&
          transaction.endOfTransactionMailConfiguration.subject !== ""
            ? transaction.endOfTransactionMailConfiguration.subject
            : t(
                "signature.accordion.parametersForm.preview.modal.subject.value",
              )
        }
        previewMail={previewMail}
      />
    </div>
  );
};

export default TransactionMailsSection;
